import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as XLSX from "xlsx";
import { HeaderEmpresaCard } from "../../../../Components/PdfComponents/HeaderEmpresaCard";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const exportarExcel = (nombreArchivo, nombreHoja, filas) => {
  const datos = filas.length > 0 ? filas : [{ "Sin datos": "" }];
  const columnas = [...new Set(datos.flatMap((d) => Object.keys(d)))];
  const ws = XLSX.utils.json_to_sheet(datos, { header: columnas });

  ws["!cols"] = columnas.map((k) => ({
    wch: Math.min(60, Math.max(k.length, ...datos.map((d) => String(d[k] ?? "").length)) + 2),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, nombreHoja);
  XLSX.writeFile(wb, `${nombreArchivo}_${moment().format("YYYY-MM-DD")}.xlsx`);
};

export const exportarExcelIndicador = ({ anio, titulo, alcanceLabel, filtros, objetivoMensual, meses, trimestres, acumuladoAnual, notas }) => {
  const filasMensual = meses.map((m) => ({
    Mes: m.etiqueta,
    Objetivo: objetivoMensual,
    Indicador: m.promedio ?? "s/d",
    Evaluaciones: m.evaluacionesContadas,
  }));
  const filasTrimestral = trimestres.map((t) => ({
    Trimestre: t.etiqueta,
    Objetivo: objetivoMensual,
    Indicador: t.promedio ?? "s/d",
    Evaluaciones: t.evaluacionesContadas,
  }));
  filasTrimestral.push({
    Trimestre: "Acumulado anual",
    Objetivo: objetivoMensual,
    Indicador: acumuladoAnual.promedio ?? "s/d",
    Evaluaciones: acumuladoAnual.evaluacionesContadas,
  });
  const filasNotas = notas.length
    ? notas.map((n) => ({ Periodo: n.etiqueta, Nota: n.texto }))
    : [{ Periodo: "-", Nota: "Sin notas registradas." }];

  const wb = XLSX.utils.book_new();
  const filasResumen = [
    { Indicador: titulo || "Indicador General de Evaluación de Proveedores", Año: anio },
    ...(filtros?.length ? filtros.map((f) => ({ "Filtro aplicado": f })) : [{ "Filtro aplicado": "Ninguno (general)" }]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasResumen), "Resumen");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasMensual), "Mensual");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasTrimestral), "Trimestral");
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filasNotas), "Notas");
  XLSX.writeFile(wb, `IndicadorEvaluacionProveedores_${anio}_${alcanceLabel}_${moment().format("YYYY-MM-DD")}.xlsx`);
};

const PALETA = {
  header: "#334155",
  cumple: "#3f7a5c",
  noCumple: "#a4453c",
  sinDato: "#94a3b8",
  filtros: "#475569",
  filtrosFondo: "#f1f5f9",
};

const PDF_STYLES = {
  header: { fontSize: 9, bold: true },
  subheader: { fontSize: 8, bold: true },
  tableExample: { margin: [0, 5, 0, 5] },
  tableHeader: { bold: true, fontSize: 12, color: "black", alignment: "center" },
  textoChico: { fontSize: 8 },
  tituloTabla: { fontSize: 8, bold: true, fillColor: "#e2e8f0", alignment: "center" },
  tituloTablaOscuro: { fontSize: 8, bold: true, fillColor: PALETA.header, color: "#ffffff", alignment: "center" },
  textoTabla: { fontSize: 8 },
  totalChico: { fontSize: 6, bold: true, fillColor: "#e2e8f0", alignment: "right" },
};

const LAYOUT_ZEBRA = {
  fillColor: (i) => (i % 2 === 0 && i !== 0 ? "#e8e8e8" : "#ffffff"),
  hLineColor: () => "#adaaaa",
  vLineColor: () => "#adaaaa",
};

const LAYOUT_SIMPLE = { hLineColor: () => "#adaaaa", vLineColor: () => "#adaaaa" };

const tablaKpis = (kpis) => {
  const visibles = kpis.filter(Boolean);
  if (visibles.length === 0) return [];
  return [
    { text: "Resumen del período", style: "subheader", margin: [0, 4, 0, 4] },
    {
      style: "tableExample",
      table: {
        widths: visibles.map(() => "*"),
        body: [
          visibles.map((k) => ({ text: k.label, style: "tituloTabla" })),
          visibles.map((k) => ({ text: String(k.value), style: "totalChico", alignment: "center", fontSize: 13 })),
        ],
      },
      layout: LAYOUT_SIMPLE,
    },
  ];
};

const seccionTabla = (titulo, columnas, filas) => {
  if (!filas || filas.length === 0) return [];
  return [
    { text: titulo, style: "subheader", fontSize: 11, margin: [0, 14, 0, 4] },
    {
      style: "tableExample",
      table: {
        headerRows: 1,
        widths: columnas.map(() => "*"),
        body: [
          columnas.map((c) => ({ text: c, style: "tituloTabla" })),
          ...filas.map((fila) => fila.map((celda) => ({ text: celda ?? "-", style: "textoTabla" }))),
        ],
      },
      layout: LAYOUT_ZEBRA,
    },
  ];
};

const colorIndicador = (valor, objetivoMensual) =>
  valor == null ? PALETA.sinDato : valor >= objetivoMensual ? PALETA.cumple : PALETA.noCumple;

// Tabla "MES | mes1 | mes2 | mes3" con filas Objetivo/Indicador
const tablaTrimestreGrid = (numTrimestre, mesesDelTrimestre, objetivoMensual) => ({
  table: {
    widths: ["30%", "23.33%", "23.33%", "23.34%"],
    body: [
      [
        { text: `TRIMESTRE ${numTrimestre}`, style: "tituloTablaOscuro" },
        ...mesesDelTrimestre.map((m) => ({ text: m.etiqueta, style: "tituloTabla" })),
      ],
      [
        { text: "Objetivo", style: "textoTabla" },
        ...mesesDelTrimestre.map(() => ({ text: `${objetivoMensual}%`, style: "textoTabla", alignment: "center" })),
      ],
      [
        { text: "Indicador", style: "textoTabla", bold: true },
        ...mesesDelTrimestre.map((m) => ({
          text: m.promedio != null ? `${m.promedio}%` : "s/d",
          style: "textoTabla",
          alignment: "center",
          bold: true,
          color: colorIndicador(m.promedio, objetivoMensual),
        })),
      ],
    ],
  },
  layout: LAYOUT_SIMPLE,
});

// Grid 2x2 de trimestres: T1/T2 arriba, T3/T4 abajo
const gridTrimestral = (meses, objetivoMensual) => {
  const porTrimestre = [meses.slice(0, 3), meses.slice(3, 6), meses.slice(6, 9), meses.slice(9, 12)];
  return [
    { text: "Indicador mensual por trimestre", style: "subheader", fontSize: 11, margin: [0, 14, 0, 6] },
    {
      columns: [
        { width: "49%", ...tablaTrimestreGrid(1, porTrimestre[0], objetivoMensual) },
        { width: "2%", text: "" },
        { width: "49%", ...tablaTrimestreGrid(2, porTrimestre[1], objetivoMensual) },
      ],
    },
    {
      margin: [0, 6, 0, 0],
      columns: [
        { width: "49%", ...tablaTrimestreGrid(3, porTrimestre[2], objetivoMensual) },
        { width: "2%", text: "" },
        { width: "49%", ...tablaTrimestreGrid(4, porTrimestre[3], objetivoMensual) },
      ],
    },
  ];
};

const tablaResultadosTrimestre = (trimestres, objetivoMensual) => [
  { text: "Resultados por trimestre", style: "subheader", fontSize: 11, margin: [0, 14, 0, 4] },
  {
    table: {
      widths: trimestres.map(() => "*"),
      body: [
        trimestres.map((t) => ({ text: t.etiqueta, style: "tituloTabla" })),
        trimestres.map((t) => ({
          text: t.promedio != null ? `${t.promedio}%` : "s/d",
          alignment: "center",
          bold: true,
          fontSize: 11,
          color: colorIndicador(t.promedio, objetivoMensual),
        })),
      ],
    },
    layout: LAYOUT_SIMPLE,
  },
];

// Barras verticales por mes con línea punteada de objetivo (canvas a mano)
const barrasConMeta = (meses, objetivoMensual) => {
  const H = 80;
  const W = 13;
  const GAP = 5;
  const items = meses.filter((m) => m.promedio != null);
  if (items.length === 0) return [];

  const rects = meses.map((m, i) => {
    if (m.promedio == null) return null;
    const alto = Math.max(2, Math.round((m.promedio / 100) * H));
    return {
      type: "rect",
      x: i * (W + GAP),
      y: H - alto,
      w: W,
      h: alto,
      color: colorIndicador(m.promedio, objetivoMensual),
      r: 2,
    };
  }).filter(Boolean);

  const yMeta = H - Math.round((objetivoMensual / 100) * H);
  const lineaMeta = {
    type: "line",
    x1: 0,
    y1: yMeta,
    x2: meses.length * (W + GAP),
    y2: yMeta,
    lineWidth: 1,
    lineColor: PALETA.header,
    dash: { length: 3, space: 2 },
  };

  return [
    { text: "Indicador mensual vs objetivo", style: "subheader", fontSize: 11, margin: [0, 14, 0, 4] },
    {
      columns: [
        { width: meses.length * (W + GAP) + 10, canvas: [...rects, lineaMeta] },
        { width: "*", text: `Objetivo: ${objetivoMensual}%`, fontSize: 7, color: PALETA.header, margin: [8, 4, 0, 0] },
      ],
    },
    {
      columns: meses.map((m) => ({ width: W + GAP, text: m.etiqueta, fontSize: 6, alignment: "center" })),
      margin: [0, 2, 0, 0],
    },
  ];
};

// Banner de filtros activos (alcance/plantilla/frecuencia)
const bannerFiltros = (filtros) => {
  if (!filtros || filtros.length === 0) return [];
  return [
    {
      margin: [0, 8, 0, 8],
      table: {
        widths: ["*"],
        body: [
          [
            {
              text: `Filtros aplicados: ${filtros.join("  ·  ")}`,
              fontSize: 9,
              bold: true,
              color: PALETA.filtros,
              fillColor: PALETA.filtrosFondo,
              margin: [8, 5, 8, 5],
            },
          ],
        ],
      },
      layout: "noBorders",
    },
  ];
};

export const descargarPdfIndicadorProveedores = ({ empresa, titulo, anio, alcanceLabel, filtros, objetivoMensual, meses, trimestres, acumuladoAnual, notas }) => {
  const terceraColumna = {
    border: [false, false, false, false],
    table: {
      headerRows: 1,
      widths: ["100%"],
      body: [
        [{ text: titulo || "Indicador de Evaluación de Proveedores", style: "subheader", alignment: "center" }],
        [{ text: `Año ${anio}`, style: "textoChico", alignment: "center" }],
        [{ text: "Fecha de Impresión:", style: "subheader", alignment: "center" }],
        [{ text: moment().locale("es").format("LL"), style: "textoChico", alignment: "center" }],
      ],
    },
    layout: "noBorders",
  };

  const dd = {
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [20, 20, 20, 40],
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["18%", "52%", "30%"],
          body: [[...HeaderEmpresaCard(empresa ?? {}, false, false, terceraColumna)]],
        },
      },
      ...bannerFiltros(filtros),
      ...tablaKpis([
        { label: "Objetivo mensual", value: `${objetivoMensual}%` },
        { label: "Acumulado anual", value: acumuladoAnual.promedio != null ? `${acumuladoAnual.promedio}%` : "s/d" },
        { label: "Evaluaciones consideradas", value: acumuladoAnual.evaluacionesContadas },
      ]),
      ...gridTrimestral(meses, objetivoMensual),
      ...barrasConMeta(meses, objetivoMensual),
      ...tablaResultadosTrimestre(trimestres, objetivoMensual),
      ...seccionTabla(
        "Notas / Acciones correctivas",
        ["Periodo", "Nota"],
        notas.filter((n) => n.texto).map((n) => [n.etiqueta, n.texto]),
      ),
    ],
    images: { logo: empresa?.logo?.base64Completa ?? "" },
    styles: PDF_STYLES,
  };

  pdfMake.createPdf(dd).download(`IndicadorEvaluacionProveedores_${anio}_${alcanceLabel}_${moment().format("YYYY-MM-DD")}`);
};
